import classNames from 'classnames/bind';
import styles from './KontaktHero.module.scss';
import { FaChevronDown, FaPhone, FaEnvelope, FaTimes } from 'react-icons/fa';
import GravityForm from '../GravityForm/GravityForm';
import { useEffect, useRef, useCallback } from 'react';
import { PrimaryButton } from '../../../components';
import useCalendly from '../../Calendly';
import { useRouter } from 'next/router';
import { getSiteUiFromPath } from '../../../lib/polylang';

let cx = classNames.bind(styles);

export default function KontaktHero({ data, onFormToggle, showForm }) {
  if (!data) return null;

  const router = useRouter();
  const siteUi = getSiteUiFromPath(router.asPath);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const overlayRef = useRef(null);
  const { openCalendlyInline } = useCalendly();

  // Handle ESC key press to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && showForm) {
      onFormToggle();
    }
  }, [showForm, onFormToggle]);

  // Handle click on overlay to close modal
  const handleOverlayClick = useCallback((e) => {
    // Only close if the click is directly on the overlay, not its children
    if (e.target === overlayRef.current) {
      onFormToggle();
    }
  }, [onFormToggle]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
      
      // Focus the close button when modal opens
      if (closeButtonRef.current) {
        setTimeout(() => {
          closeButtonRef.current.focus();
        }, 100);
      }
    } else {
      document.body.style.overflow = 'auto';
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showForm, handleKeyDown]);

  // Create an array from the contactBoxes data with subtitles
  const contactBoxes = [
    {
      type: 'form',
      title: siteUi.fillOutForm,
      subtitle: siteUi.weWillContactYou,
      isButton: true,
      icon: <FaChevronDown />
    },
    {
      type: 'phone',
      title: siteUi.callUs,
      subtitle: `${data.contactBoxes?.phoneNumber}`,
      value: data.contactBoxes?.phoneNumber,
      link: `tel:${data.contactBoxes?.phoneNumber}`,
      icon: <FaPhone />
    },
    {
      type: 'email',
      title: siteUi.sendUsEmail,
      subtitle: data.contactBoxes?.email,
      value: data.contactBoxes?.email,
      link: `mailto:${data.contactBoxes?.email}`,
      icon: <FaEnvelope />
    }
  ];

  return (
    <>
      <section className={cx('heroSection')}>
        <div className={cx('container')}>
          <div className={cx('content')}>
            {data.smallTopHeader && (
              <p className={cx('smallHeader')}>{data.smallTopHeader}</p>
            )}
            {data.mainHeaderText && (
              <h1>{data.mainHeaderText}</h1>
            )}
            <div className={cx('textButtonWrapper')}>
              {data.subText && (
                <p className={cx('subText')}>{data.subText}</p>
              )}
              <div className={cx('buttonWrapper')}>
                <PrimaryButton
                  text={siteUi.bookMeetingHeader}
                  onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    openCalendlyInline(e, 'https://calendly.com/sales-mediabooster/intromote-digital-markedsforing'); 
                  }}
                />
              </div>
            </div>
          </div>

          <div className={cx('contactBoxes')}>
            {contactBoxes.map((box, index) => (
              <div key={index} className={cx('contactBox')}>
                {box.isButton ? (
                  <button 
                    onClick={onFormToggle}
                    className={cx('boxButton', { active: showForm })}
                    aria-expanded={showForm}
                    aria-label={box.title}
                  >
                    <div className={cx('boxContent')}>
                      <h3 className={cx('noUnderline')}>{box.title}</h3>
                      <p className={cx('noUnderline')}>{box.subtitle}</p>
                    </div>
                    <span className={cx('icon', { rotated: showForm })}>{box.icon}</span>
                  </button>
                ) : (
                  <a 
                    href={box.link} 
                    className={cx('boxLink', 'noUnderline')}
                    aria-label={`${box.title}: ${box.value}`}
                  >
                    <div className={cx('boxContent')}>
                      <h3 className={cx('noUnderline')}>{box.title}</h3>
                      <p className={cx('noUnderline')}>{box.subtitle}</p>
                    </div>
                    <span className={cx('icon')}>{box.icon}</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal Overlay */}
      {showForm && (
        <div 
          className={cx('modalOverlay')}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modalTitle"
          onClick={handleOverlayClick}
          ref={overlayRef}
        >
          <div 
            className={cx('modalContent')}
            ref={modalRef}
            tabIndex="-1"
            onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling up and closing the modal
          >
            <button 
              className={cx('closeButton')} 
              onClick={onFormToggle}
              aria-label={siteUi.closeForm}
              ref={closeButtonRef}
            >
              <FaTimes />
            </button>
            <div className={cx('modalFormContainer')}>
              <h2 id="modalTitle" className={cx('modalTitle')}>{siteUi.contactUs}</h2>
              <GravityForm formId={3} />
            </div>
          </div>
        </div>
      )}
    </>
  );
} 